import { useState } from 'react';
import { User, Mail, ShieldAlert, ArrowLeft, Loader2, Save } from 'lucide-react';

const Profile = ({ user, onBack, onLogout, onUserUpdate }) => {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // 1. Update Logic (Username/Email)
  const handleUpdate = async (e) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: 'Updating...' });
    
    try {
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        // We include an empty password to match the Backend Schema (UserCreate)
        body: JSON.stringify({
          ...formData,
          password: "" 
        }),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setStatus({ type: 'success', message: 'Profile updated successfully!' });
        
        // --- CRITICAL CHANGE ---
        // Notify App.js about the new data so the Header updates instantly
        if (onUserUpdate) {
            onUserUpdate(data);
        }
      } else {
        // Handling FastAPI/Pydantic error objects
        const errorMessage = data.detail && typeof data.detail === 'string' 
          ? data.detail 
          : "Invalid data (check your inputs)";
          
        setStatus({ type: 'error', message: errorMessage });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Server error. Please try again later.' });
    }
  };

  // 2. Delete Logic
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch('/api/users/me', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        onLogout(); 
      }
    } catch (err) {
      alert("Error during account deletion.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-73px)] bg-slate-950 text-slate-200 p-6">
      <div className="max-w-2xl mx-auto">
        
        {/* Back Button */}
        <button onClick={onBack} className="flex items-center text-slate-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Transcriber
        </button>

        <h1 className="text-3xl font-bold text-white mb-8 text-center">My Profile</h1>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <form onSubmit={handleUpdate} className="p-8 space-y-6">
            
            {/* Username Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-400 flex items-center">
                <User className="w-4 h-4 mr-2" /> Username
              </label>
              <input 
                type="text"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-all text-white"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-400 flex items-center">
                <Mail className="w-4 h-4 mr-2" /> Email Address
              </label>
              <input 
                type="email"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 focus:border-emerald-500 outline-none transition-all text-white"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <button 
              type="submit"
              disabled={status.type === 'loading'}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center disabled:bg-slate-700"
            >
              {status.type === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </>
              )}
            </button>

            {status.message && (
              <p className={`text-center text-sm font-medium ${status.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                {status.message}
              </p>
            )}
          </form>

          {/* Danger Zone */}
          <div className="bg-slate-950/50 border-t border-slate-800 p-8">
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-rose-500/10 rounded-lg text-rose-500">
                <ShieldAlert className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-rose-500 font-bold">Danger Zone</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Once deleted, your account and all your transcriptions cannot be recovered.
                </p>
                
                {!showConfirmDelete ? (
                  <button 
                    onClick={() => setShowConfirmDelete(true)}
                    className="mt-4 text-xs font-bold text-slate-500 hover:text-rose-500 uppercase tracking-widest transition-colors"
                  >
                    Delete my account
                  </button>
                ) : (
                  <div className="mt-4 flex items-center space-x-4 animate-in slide-in-from-left-2 duration-300">
                    <button 
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all"
                    >
                      {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, delete permanently"}
                    </button>
                    <button 
                      onClick={() => setShowConfirmDelete(false)}
                      className="text-slate-400 text-xs font-bold hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;